import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight, Trash2, Workflow } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { itemVariants } from "./motion";
import type { AutomationRule, QuickReply, RuleAction } from "../mockAutomations";
import { cn } from "@/lib/utils";

interface AutomationRulesCardProps {
  rules: AutomationRule[];
  quickReplies: QuickReply[];
  onAdd: (rule: Omit<AutomationRule, "id">) => void;
  onRemove: (id: string) => void;
}

const ACTION_LABEL: Record<RuleAction, string> = {
  reply: "Reply with template",
  tag: "Tag lead",
  escalate: "Escalate to human",
};

const ACTION_BADGE_STYLE: Record<RuleAction, string> = {
  reply: "border-emerald-200/80 bg-emerald-50 text-emerald-700",
  tag: "border-indigo-200/80 bg-indigo-50 text-indigo-700",
  escalate: "border-amber-200/80 bg-amber-50 text-amber-700",
};

export function AutomationRulesCard({ rules, quickReplies, onAdd, onRemove }: AutomationRulesCardProps) {
  const [adding, setAdding] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [action, setAction] = useState<RuleAction>("reply");
  const [actionDetail, setActionDetail] = useState("");

  const resetForm = () => {
    setKeyword("");
    setAction("reply");
    setActionDetail("");
    setAdding(false);
  };

  const save = () => {
    if (!keyword.trim()) return;
    const detail =
      action === "reply"
        ? actionDetail || quickReplies[0]?.shortcut || ""
        : action === "tag"
        ? actionDetail || "Tag as lead"
        : "Escalate to human";
    onAdd({ keyword: keyword.trim(), action, actionDetail: detail });
    resetForm();
  };

  return (
    <motion.div variants={itemVariants} className="h-full">
      <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col">
        {/* Pod Header */}
        <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-4 py-2.5 gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
              <Workflow className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Automation Rules
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200/80 dark:border-indigo-900/60 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.2 text-[9px] font-bold text-indigo-700 dark:text-indigo-400">
              {rules.length} Rules
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => setAdding(true)}
            className="h-7.5 px-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold shadow-xs gap-1 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add Rule
          </Button>
        </div>

        <div className="p-3 bg-white/70 dark:bg-slate-900/70 space-y-2 flex-1">
          <AnimatePresence initial={false}>
            {adding && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-2 rounded-xl border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/30 dark:bg-indigo-950/30 p-2.5 mb-2">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input
                      placeholder="Keyword, e.g. refund"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      className="h-8.5 text-xs bg-white dark:bg-slate-900 rounded-xl"
                    />
                    <Select
                      value={action}
                      onChange={(e) => setAction(e.target.value as RuleAction)}
                      className="h-8.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs"
                    >
                      <option value="reply">Reply with template</option>
                      <option value="tag">Tag lead</option>
                      <option value="escalate">Escalate to human</option>
                    </Select>
                  </div>
                  {action === "reply" && (
                    <Select
                      value={actionDetail}
                      onChange={(e) => setActionDetail(e.target.value)}
                      className="h-8.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs"
                    >
                      <option value="">Choose a quick reply…</option>
                      {quickReplies.map((qr) => (
                        <option key={qr.id} value={qr.shortcut}>{qr.shortcut}</option>
                      ))}
                    </Select>
                  )}
                  {action === "tag" && (
                    <Input
                      placeholder="Tag name, e.g. Tag as B2B lead"
                      value={actionDetail}
                      onChange={(e) => setActionDetail(e.target.value)}
                      className="h-8.5 text-xs bg-white dark:bg-slate-900 rounded-xl"
                    />
                  )}
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={resetForm} className="h-7.5 px-3 rounded-full text-xs">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={save} className="h-7.5 px-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold">
                      Save Rule
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {rules.length === 0 && !adding && (
            <p className="py-4 text-center text-xs text-slate-400 dark:text-slate-500">No automation rules configured yet.</p>
          )}

          <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-0.5">
            {rules.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-2.5 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
                <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs">
                  <span className="text-slate-400 dark:text-slate-500 text-[11px]">If contains</span>
                  <span className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-1.5 py-0.2 font-mono text-[11px] font-bold text-slate-800 dark:text-slate-200">
                    {rule.keyword}
                  </span>
                  <ArrowRight className="h-3 w-3 shrink-0 text-slate-400" />
                  <span className={cn("inline-flex items-center rounded-full border px-2 py-0.2 text-[10px] font-bold", ACTION_BADGE_STYLE[rule.action])}>
                    {ACTION_LABEL[rule.action]}
                  </span>
                  {rule.action !== "escalate" && (
                    <span className="truncate text-[11px] text-slate-400 dark:text-slate-500 font-medium">({rule.actionDetail})</span>
                  )}
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-slate-400 hover:text-rose-600" onClick={() => onRemove(rule.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
